import React, { useCallback } from 'react';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import { TextInput } from '@/common/components/Inputs';
import styles from '@/modules/general-notes/styles.less';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputLabel } from '@mui/material';
import ConfirmModal from '@/common/containers/Modals/ConfirmModal';

const MAX_CHECKLIST_ITEMS = 50;

const NoteDetailsField = ({ disableCheckbox = false }) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext() || {};
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'checklistItems',
  });
  const checklistLimitReached = fields.length >= MAX_CHECKLIST_ITEMS;

  const handleAdd = useCallback(() => {
    if (checklistLimitReached) return;

    const value = getValues('newDetail')?.trim();
    if (!value) return;
    append({ label: value });
    setValue('newDetail', '');
  }, [append, checklistLimitReached, getValues, setValue]);

  return (
    <div className={styles.noteDetail}>
      <InputLabel
        sx={{
          fontFamily: 'Red Hat Text, sans-serif',
          fontWeight: 500,
          fontSize: '18px',
          lineHeight: '24px',
          color: '#000000',
          position: 'static',
          transform: 'none',
        }}
      >
        {t('Note details')}
      </InputLabel>
      {fields.length > 0 && (
        <>
          {fields.map((field, index) => (
            <div className={styles.noteDetailRow} key={field.id}>
              <Controller
                name={`checklistItems.${index}.done`}
                control={control}
                render={({ field: checkboxField }) => (
                  <TextInput
                    name={`checklistItems.${index}.label`}
                    className={styles.noteFormRowInput}
                    multiline
                    minRows={1}
                    maxRows={4}
                    sx={{
                      '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
                        alignItems: 'flex-start',
                        padding: 0,
                      },
                      '& .MuiInputAdornment-root': {
                        alignSelf: 'flex-start',
                        marginTop: '14px',
                        marginRight: '8px',
                        marginBottom: 0,
                      },
                      '& .MuiOutlinedInput-inputMultiline': {
                        padding: '16px 12px 16px 0 !important',
                      },
                    }}
                    inputProps={{ maxLength: 300 }}
                    InputProps={{
                      startAdornment: (
                        <Checkbox
                          checked={!!checkboxField.value}
                          onChange={(event) =>
                            checkboxField.onChange(event.target.checked)
                          }
                          disabled={disableCheckbox}
                          size="small"
                          sx={{
                            margin: 'auto',
                            padding: '2px',
                            mx: '8px',
                            alignSelf: 'flex-start',
                          }}
                        />
                      ),
                    }}
                  />
                )}
              />
              <ConfirmModal
                confirmText={t('Delete')}
                title={t('Delete checklist item?')}
                description={t(
                  'Are you sure you want to delete this checklist item? This action can’t be undone.'
                )}
                type="delete"
              >
                {(confirm) => (
                  <IconButton
                    type="button"
                    color="error"
                    sx={{ marginTop: '8px' }}
                    className={styles.noteFormRowRemove}
                    onClick={confirm(() => remove(index))}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </ConfirmModal>
            </div>
          ))}
        </>
      )}
      {!checklistLimitReached && (
        <div className={styles.noteFormAdd}>
          <TextInput
            name="newDetail"
            placeholder={t('Enter note details')}
            className={styles.noteFormAddInput}
            multiline
            minRows={1}
            maxRows={4}
            inputProps={{ maxLength: 300 }}
            rules={{
              maxLength: {
                value: 300,
                message: t('Must be shorter than or equal to 300 characters'),
              },
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleAdd();
              }
            }}
          />
          <Button
            type="button"
            color="success"
            variant="contained"
            className={styles.noteFormAddBtn}
            onClick={handleAdd}
            disabled={checklistLimitReached}
          >
            <AddIcon />
          </Button>
        </div>
      )}
    </div>
  );
};

export default NoteDetailsField;
